export type ParquetMetricStage =
  | 'queue-wait'
  | 'base-row-delivery'
  | 'response-delivery'
  | 'response-accumulation'
  | 'cancellation'
  | 'worker-restart'
  | 'range-buffer-copy'
  | 'metadata-decode'
  | 'row-decode'
  | 'row-normalization'
  | 'incremental-merge'
  | 'downstream-consumption';

export interface ParquetStageMetric {
  resourceId: string;
  stage: ParquetMetricStage;
  thread: 'main' | 'worker';
  durationMs: number;
  rowCount?: number;
  columnCount?: number;
}

export type ParquetMetricSink = (metric: ParquetStageMetric) => void;

let metricSink: ParquetMetricSink | undefined;

export const setParquetMetricSink = (sink?: ParquetMetricSink) => {
  metricSink = sink;
};

export const isParquetMetricSinkEnabled = () => Boolean(metricSink);

export const emitParquetMetric = (metric: ParquetStageMetric) => {
  metricSink?.(metric);
};

export const getParquetResourceId = (url: string): string => {
  let resourceName = 'invalid-resource';
  try {
    const parsedUrl = new URL(url);
    resourceName = `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    // Keep malformed resources opaque as well.
  }

  let hash = 2_166_136_261;
  for (let index = 0; index < resourceName.length; index += 1) {
    hash ^= resourceName.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return `parquet-${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

export const measureParquetStage = <Result>(
  metric: Omit<ParquetStageMetric, 'durationMs'>,
  operation: () => Result
): Result => {
  if (!metricSink) return operation();

  const startedAt = performance.now();
  try {
    return operation();
  } finally {
    metricSink({ ...metric, durationMs: performance.now() - startedAt });
  }
};

export const measureParquetStageAsync = async <Result>(
  metric: Omit<ParquetStageMetric, 'durationMs'>,
  operation: () => Promise<Result>
): Promise<Result> => {
  if (!metricSink) return operation();

  const startedAt = performance.now();
  try {
    return await operation();
  } finally {
    metricSink({ ...metric, durationMs: performance.now() - startedAt });
  }
};
